import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isArReady } from "@/lib/productUtils";
import { arDisplayStatus } from "@/lib/assetStatus";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const CATEGORY_LABELS: Record<string, string> = {
  gas_fireplace: "Gas Fireplace", wood_fireplace: "Wood Fireplace",
  pellet_stove: "Pellet Stove", electric_fireplace: "Electric Fireplace",
  fireplace_insert: "Fireplace Insert", gas_logs: "Gas Logs",
  chimney_venting: "Chimney & Venting", fireplace_accessory: "Hearth Accessory",
  patio_furniture: "Patio Furniture", outdoor_kitchen: "Outdoor Kitchen",
  pergola_gazebo: "Pergola / Gazebo", fire_pit: "Fire Pit",
  patio_heater: "Patio Heater", outdoor_accessory: "Outdoor Accessory",
  gas_grill: "Gas Grill", charcoal_grill: "Charcoal Grill",
  pellet_grill: "Pellet Grill", kamado_grill: "Kamado Grill",
  built_in_grill: "Built-in Grill", bbq_accessory: "BBQ Accessory",
};

// Keyed on ArDisplayStatus, never on a raw asset_status — `ready` and
// `complete` are both terminal and both belong under one label. See
// src/lib/assetStatus.ts.
const AR_META: Record<string, { label: string; dot: string }> = {
  ready:      { label: "AR Ready",   dot: "bg-green-500" },
  processing: { label: "Processing", dot: "bg-blue-500 animate-pulse" },
  pending:    { label: "Pending",    dot: "bg-yellow-500" },
  failed:     { label: "Failed",     dot: "bg-red-500" },
};

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface DashStats {
  totalProducts: number;
  arReady: number;
  processing: number;
  draft: number;
  withSpecs: number;
  qaApprovedPct: number | null;
}

interface RecentProduct {
  id: string;
  name: string;
  category: string | null;
  manufacturer_name: string | null;
  asset_status: string | null;
  glb_assembly_status: string | null;
  glb_pbr_url: string | null;
  usdz_pbr_url: string | null;
  consumer_specs_json: Record<string, unknown> | null;
  qa_status: string | null;
  active: boolean;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function StatCard({
  label, value, sub, accent, loading, suffix,
}: {
  label: string; value: number | null; sub?: string; accent?: string; loading: boolean; suffix?: string;
}) {
  if (loading) {
    return <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 h-[84px] animate-pulse" />;
  }
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-1">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-semibold tabular-nums ${accent ?? "text-white"}`}>
        {value ?? "—"}{suffix && value != null ? <span className="text-xl">{suffix}</span> : null}
      </p>
      {sub && <p className="text-xs text-gray-600">{sub}</p>}
    </div>
  );
}

function ArDot({ status }: { status: string | null }) {
  const meta = AR_META[status ?? ""] ?? { label: "No AR", dot: "bg-gray-600" };
  return (
    <span className="flex items-center gap-1.5 text-xs text-gray-400">
      <span className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-800">
      <table className="w-full text-sm">
        <thead className="bg-gray-900/60">
          <tr className="border-b border-gray-800">
            {["Name", "Category", "AR Status", "Specs", "Added"].map((h) => (
              <th key={h} className="px-4 py-3 text-left font-medium text-gray-400">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 4 }).map((_, i) => (
            <tr key={i} className="border-t border-gray-800">
              {Array.from({ length: 5 }).map((_, j) => (
                <td key={j} className="px-4 py-3">
                  <div className="h-3.5 rounded bg-gray-800 animate-pulse" style={{ width: j === 0 ? "140px" : "80px" }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashStats | null>(null);
  const [recent, setRecent] = useState<RecentProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      // Fetch all products for stats + recent (limit 200 for stat accuracy)
      const res = await fetch(
        `${SB_URL}/rest/v1/products` +
          `?select=id,name,category,asset_status,glb_assembly_status,glb_pbr_url,usdz_pbr_url,consumer_specs_json,qa_status,active,created_at,manufacturers(name)` +
          `&order=created_at.desc&limit=200`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` } }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to load products (${res.status}): ${text.slice(0, 120)}`);
      }

      const data: Array<RecentProduct & { manufacturers?: { name: string } | null }> = await res.json();

      const normalized = data.map((p) => ({
        ...p,
        manufacturer_name: p.manufacturers?.name ?? null,
      }));

      const withQa = normalized.filter((p) => p.qa_status != null);
      const qaApprovedPct = withQa.length > 0
        ? Math.round((withQa.filter((p) => p.qa_status === "approved").length / withQa.length) * 100)
        : null;

      setStats({
        totalProducts: normalized.length,
        // AR ready: pipeline completed
        arReady: normalized.filter((p) => isArReady(p)).length,
        // Processing: in pipeline (pending or processing)
        processing: normalized.filter((p) => {
          const s = arDisplayStatus(p);
          return s === "processing" || s === "pending";
        }).length,
        // Draft: not yet published
        draft: normalized.filter((p) => !p.active).length,
        // With specs: Claude has extracted specs
        withSpecs: normalized.filter((p) => !!p.consumer_specs_json).length,
        // QA approved %: fraction of QA-run products that passed
        qaApprovedPct,
      });

      setRecent(normalized.slice(0, 8));
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function formatTime(d: Date) {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          {lastUpdated && !loading && (
            <p className="mt-0.5 text-xs text-gray-500">
              Last updated {formatTime(lastUpdated)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchData(true)}
            disabled={loading || refreshing}
            className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <Link
            to="/admin/products/new"
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition"
          >
            + New Product
          </Link>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-xl border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
          {error}
          <button onClick={() => fetchData()} className="ml-3 underline underline-offset-2 hover:text-red-300">
            Retry
          </button>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Total Products" value={stats?.totalProducts ?? 0} loading={loading} />
        <StatCard
          label="AR Ready" value={stats?.arReady ?? 0}
          accent="text-green-400" sub="pipeline complete" loading={loading}
        />
        <StatCard
          label="Processing" value={stats?.processing ?? 0}
          accent="text-blue-400" sub="in pipeline" loading={loading}
        />
        <StatCard
          label="Drafts" value={stats?.draft ?? 0}
          accent="text-yellow-400" sub="not published" loading={loading}
        />
        <StatCard
          label="With Specs" value={stats?.withSpecs ?? 0}
          accent="text-orange-400" sub="Claude extracted" loading={loading}
        />
        <StatCard
          label="QA Approved" value={stats?.qaApprovedPct ?? null}
          suffix="%" accent="text-teal-400" sub="of QA-run products" loading={loading}
        />
      </div>

      {/* Recent products */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Recent Products</h2>
          <Link to="/admin/products" className="text-xs text-orange-400 hover:text-orange-300 transition">
            View all →
          </Link>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : recent.length === 0 ? (
          <div className="rounded-xl border border-gray-800 py-14 text-center space-y-4">
            <p className="text-sm text-gray-500">No products yet.</p>
            <Link
              to="/admin/products/new"
              className="inline-block rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition"
            >
              + Add your first product
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60">
                <tr className="border-b border-gray-800">
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Category</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Manufacturer</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">AR Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Specs</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">Added</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/admin/products/${p.id}`)}
                    className="border-t border-gray-800 hover:bg-gray-900/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3 font-medium max-w-[180px]">
                      <span className="block truncate">{p.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {CATEGORY_LABELS[p.category ?? ""] ?? p.category ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {p.manufacturer_name ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <ArDot status={arDisplayStatus(p)} />
                    </td>
                    <td className="px-4 py-3">
                      {p.consumer_specs_json
                        ? <span className="text-xs text-green-400">✓ Extracted</span>
                        : <span className="text-xs text-gray-600">—</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${p.active ? "text-green-400" : "text-yellow-500"}`}>
                        {p.active ? "Active" : "Draft"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Link
          to="/admin/products/new"
          className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 hover:border-gray-700 transition group"
        >
          <p className="text-sm font-medium text-gray-200 group-hover:text-orange-400 transition">New Product →</p>
          <p className="text-xs text-gray-500 mt-1">Upload spec sheet, Claude auto-fills</p>
        </Link>
        <Link
          to="/admin/pipeline"
          className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 hover:border-gray-700 transition group"
        >
          <p className="text-sm font-medium text-gray-200 group-hover:text-orange-400 transition">View Pipeline →</p>
          <p className="text-xs text-gray-500 mt-1">Monitor AR processing queue</p>
        </Link>
        <Link
          to="/admin/manufacturers"
          className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 hover:border-gray-700 transition group"
        >
          <p className="text-sm font-medium text-gray-200 group-hover:text-orange-400 transition">Manage Manufacturers →</p>
          <p className="text-xs text-gray-500 mt-1">Add and edit manufacturer records</p>
        </Link>
      </div>
    </div>
  );
}
